import React from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { DroneEntity } from '@src/core/features';
import { ArcSocketContext } from '@src/core/socket';
import {
  clearMissionRouteContext,
  refreshAfterMissionEnded,
} from '@src/features/drone/utils/mission-route-context';

export function useMissionRouteClearance(
  drone: DroneEntity | null | undefined,
): { clearMissionRoute: () => void } {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { arcSocket, readyState } = React.useContext(ArcSocketContext);
  const serial = drone?.serialNumber;

  const clearMissionRoute = React.useCallback(() => {
    clearMissionRouteContext(dispatch, setSearchParams, searchParams, drone);
    refreshAfterMissionEnded(dispatch);
  }, [dispatch, setSearchParams, searchParams, drone]);

  React.useEffect(() => {
    const ws = arcSocket();
    if (!ws || !serial) return;

    const onMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data ?? '{}') as {
          type?: string;
          status?: string;
          command?: string;
          serialNumber?: string;
        };
        if (data.type !== 'COMMAND_STATUS') return;
        if (String(data.status ?? '').toUpperCase() !== 'SUCCESS') return;
        if (data.serialNumber && data.serialNumber !== serial) return;
        if (String(data.command ?? '').toUpperCase() !== 'END_MISSION') return;
        clearMissionRoute();
      } catch {
        // ignore malformed websocket payloads
      }
    };

    ws.addEventListener('message', onMessage);
    return () => ws.removeEventListener('message', onMessage);
  }, [arcSocket, readyState, serial, clearMissionRoute]);

  return { clearMissionRoute };
}
